import _ from 'lodash';
import { format } from '@breezeway/brzw-react-core';
import { USER_ROLES } from './roles';
import { getCloudinaryUrl } from './image';

/**
 * @param {Object} person
 * @return {String}
 */
export const personName = person =>
  _.get(person, 'user.full_name') || _.get(person, 'company.name') || _.get(person, 'full_name');

/**
 * @param {Object} person
 * @return {Boolean}
 */
export const isServicePartner = person =>
  _.isEqual(_.get(person, 'type_role.code'), USER_ROLES.SERVICE_PARTNER);

/**
 * @param {Object} person
 * @return {Boolean}
 */
export const isCompanyServicePartner = person =>
  isServicePartner(person) && !_.isEmpty(_.get(person, 'company'));

/**
 * @param {Object} person
 * @return {String}
 */
export const personEmails = person => {
  const emails = _.get(person, 'emails', []);
  const companyPersonEmails = _.join(_.map(emails, email => email.email), ', ');
  const shouldUseCompanyPersonEmails = !isCompanyServicePartner(person);

  return _.get(person, 'user.email', shouldUseCompanyPersonEmails ? companyPersonEmails : '');
};

/**
 * @param {Object} person
 * @return {String}
 */
export const personPhoneNumbers = person => {
  let phoneNumbers = _.get(person, 'user.mobile_phone_number', {});

  if (_.isEmpty(phoneNumbers.phone_number)) {
    phoneNumbers = _.get(person, 'phone_numbers');
  } else if (_.isObject(phoneNumbers)) {
    return format.phone.formatNumberByCountry(phoneNumbers.phone_number, phoneNumbers.country_code);
  }

  if (_.isEmpty(phoneNumbers)) {
    return '';
  }

  return _.join(
    _.map(phoneNumbers, phone =>
      format.phone.formatNumberByCountry(phone.phone_number, phone.country_code),
    ),
    ', ',
  );
};

/**
 * @param {Object} person
 * @return {String}
 */
export const personGroups = person => {
  const groups = _.get(person, 'groups', []);
  const groupsAllAccess = _.get(person, 'group_all_access', false);

  if (!_.isEmpty(groups)) {
    return _.join(_.map(groups, 'name'), ', ');
  } else if (groupsAllAccess) {
    return 'All groups';
  }

  return 'Not selected';
};

/**
 * @param {Object} person
 * @return {String}
 */
export const personDepartments = person =>
  _.join(_.map(_.get(person, 'type_departments', []), typeDepartment => typeDepartment.name), ', ');

/**
 * @param {Object} person
 * @return {String}
 */
export const personRoles = person => _.get(person, 'type_role.name', '');

/**
 * @param {Object} person
 * @return {Number}
 */
export const personTaskCount = person => {
  const taskCount = _.get(person, 'task_count');

  return taskCount > 0 ? taskCount : null;
};

/**
 * @param {Array} people
 * @param {Boolean} externalPeopleEnabled
 * @return {Array}
 */
export const filterAssignable = (people, externalPeopleEnabled) =>
  _.filter(people, person => {
    const isAssignable = _.get(person, 'is_assignable');
    return isAssignable || (externalPeopleEnabled && isServicePartner(person));
  });

/**
 * @param {Array} assignments
 * @return {Array}
 */
export const filterAssignableAssignments = assignments =>
  _.filter(assignments, 'company_person.is_assignable');

/**
 * @param {Array} people
 * @param {Boolean} shouldBeAssignableOnly
 * @param {Boolean} externalPeopleEnabled
 * @return {Array}
 */
export const preparePeopleToSelect = (
  people,
  shouldBeAssignableOnly = true,
  externalPeopleEnabled = false,
) => {
  let filteredActiveUsers = _.filter(people, person => _.get(person, 'active'));

  if (shouldBeAssignableOnly) {
    filteredActiveUsers = filterAssignable(filteredActiveUsers, externalPeopleEnabled);
  }

  return _.map(filteredActiveUsers, person => ({
    code: person.id,
    label: personName(person),
    value: person.id,
  }));
};

/**
 * @param {Object} task
 * @param {Array.<Object>} people
 * @return {Array}
 */
export const getCurrentlyAssignedPeople = (task, people) => {
  const assignedPeople = _.get(task, 'people', []);
  return _.map(assignedPeople, person => ({
    ..._.omit(person, 'company_people_id'),
    company_person: _.find(people, { id: _.get(person, 'company_people_id') }),
  }));
};

/**
 * @param {Object} task
 * @return {Array.<String>}
 */
export const getCurrentlyAssignedPeopleIds = task => {
  const assignedPeople = _.get(task, 'people', []);
  return _.map(assignedPeople, 'company_people_id');
};

/**
 * @param {Object} person
 * @return {Boolean}
 */
export const doesPersonHasAvailability = (person, activeDate) => {
  const currentDay = _.toLower(activeDate.format('dddd'));
  const currentAvailability = _.get(person, ['availability', currentDay], []);
  return !_.isEmpty(currentAvailability);
};

const SPACE_CHAR = ' ';
const EMPTY_STRING = '';

export const getInitials = (name, user) => {
  let userName = '';
  const firstName = _.chain(user)
    .get('first_name', EMPTY_STRING)
    .split(SPACE_CHAR)
    .head()
    .value();
  const lastName = _.chain(user)
    .get('last_name', EMPTY_STRING)
    .split(SPACE_CHAR)
    .head()
    .value();

  if (!userName && (firstName || lastName)) {
    userName = _.join([firstName, lastName], SPACE_CHAR);
  } else if (!userName) {
    userName = name;
  }

  const namesArray = _.chain(userName)
    .replace(/[^\w ]/gi, EMPTY_STRING)
    .split(SPACE_CHAR);

  if (_.size(namesArray) > 1) {
    return namesArray
      .filter(word => !_.isEmpty(word))
      .slice(0, 2)
      .map(word => _.head(word))
      .join('')
      .toUpper()
      .value();
  }

  return namesArray
    .head()
    .slice(0, 2)
    .join('')
    .toUpper()
    .value();
};

export const getCompanyInitials = name =>
  _.chain(name)
    .replace(/[^\w ]/gi, EMPTY_STRING)
    .first()
    .toUpper()
    .value();

export const getAvatarFromPerson = person => {
  const avatar = {};
  const company = _.get(person, 'company');
  const user = _.get(person, 'user');
  const name = personName(person);

  if (!_.isEmpty(company)) {
    const logoBucket = _.get(company, 'logo.bucket');
    const photoKey = _.get(company, 'logo.photo_key');
    if (logoBucket && photoKey) {
      _.set(avatar, 'url', getCloudinaryUrl(`${logoBucket}/${photoKey}`));
    }

    _.set(avatar, 'initials', getCompanyInitials(_.get(company, 'name')));
  }

  if (name || user) {
    _.set(avatar, 'initials', getInitials(name, user));
  }

  return avatar;
};
